import { AnyAction } from "redux"
import axios, { AxiosError } from "axios"
import { Dispatch, RNBThunkAction } from "../store.config"
import { reloadInputMaskAfterRebindThunk } from "./inputMask.ducks"

export interface RebindContextPath {
  id: number
  get_ocr_orientation_angle: string
  get_cloudfront_signed_url: string
  get_cloudfront_preview_signed_url: string
}

export interface DocumentInFullDocumentAPI {
  document_id: number
  full_document_id: number
}

export interface DocumentInFullDocument extends DocumentInFullDocumentAPI {
  is_modified: boolean
}

export interface RebindContextAPI {
  company_id: number
  id: number
  documents_in_full_docs: DocumentInFullDocumentAPI[][]
  documents: number[]
  is_one_document: boolean
  paths: RebindContextPath[]
  pages_count: number
}

export interface RebindContext {
  company_id: number
  id: number
  documents_in_full_docs: DocumentInFullDocument[][]
  documents: number[]
  is_one_document: boolean
  paths: RebindContextPath[]
  pages_count: number
}

export type SplitRequestStatus = "idle" | "loading"

const enum UserRebindsActionsEnum {
  LOAD_BIND_ATTEMPT = "USER_REBINDS/loadBindAttempt",
  LOAD_BIND_SUCCESS = "USER_REBINDS/loadBindSuccess",
  LOAD_BIND_FAILURE = "USER_REBINDS/loadBindFailure",
  TOGGLE_DISPLAY_REBIND_CONTENT = "USER_REBINDS/toggleDisplayRebindContent",
  SAVE_INITIAL_DOCUMENTS_IN_FULL_DOCS = "USER_REBINDS/saveInitialDocumentsInFullDocs",
  UPDATE_DOCUMENTS_IN_FULL_DOCS = "USER_REBINDS/updateDocumentsInFullDocs",
  RESET_DOCUMENTS_IN_FULL_DOCS = "USER_REBINDS/resetDocumentsInFullDocs",
  REBIND_ATTEMPT = "USER_REBINDS/rebindAttempt",
  REBIND_SUCCESS = "USER_REBINDS/rebindSuccess",
  REBIND_FAILURE = "USER_REBINDS/rebindFailure",
  SET_VIEWING_FULL_DOCUMENT_ID = "USER_REBINDS/setViewingFullDocumentId",
  SET_SELECTED_DOCUMENT_ID = "USER_REBINDS/setSelectedDocumentId",
  TOGGLE_SPLITTING = "USER_REBINDS/toggleSplitting",
  SET_SPLIT_REQUEST_STATUS = "USER_REBINDS/setSplitRequestStatus",
}

export const loadBindAttemptAction = () =>
  ({ type: UserRebindsActionsEnum.LOAD_BIND_ATTEMPT } as const)

export const loadBindSuccessAction = (payload: RebindContext) =>
  ({ type: UserRebindsActionsEnum.LOAD_BIND_SUCCESS, payload } as const)

export const loadBindFailureAction = () =>
  ({
    type: UserRebindsActionsEnum.LOAD_BIND_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "user-rebinds.load.error.title",
      bodyKey: "user-rebinds.load.error.body",
    },
  } as const)

export const toggleDisplayRebindContentAction = (
  displayRebindContent: boolean
) =>
  ({
    type: UserRebindsActionsEnum.TOGGLE_DISPLAY_REBIND_CONTENT,
    payload: { displayRebindContent },
  } as const)

export const saveInitialDocumentsInFullDocsAction = (
  initialDocumentsInFullDocs: DocumentInFullDocument[][]
) =>
  ({
    type: UserRebindsActionsEnum.SAVE_INITIAL_DOCUMENTS_IN_FULL_DOCS,
    payload: { initialDocumentsInFullDocs },
  } as const)

export const updateDocumentsInFullDocsAction = (
  documentsInFullDocs: DocumentInFullDocument[][]
) =>
  ({
    type: UserRebindsActionsEnum.UPDATE_DOCUMENTS_IN_FULL_DOCS,
    payload: { documentsInFullDocs },
  } as const)

export const resetDocumentsInFullDocsAction = () =>
  ({
    type: UserRebindsActionsEnum.RESET_DOCUMENTS_IN_FULL_DOCS,
  } as const)

export const rebindAttemptAction = () =>
  ({ type: UserRebindsActionsEnum.REBIND_ATTEMPT } as const)

export const rebindSuccessAction = () =>
  ({
    type: UserRebindsActionsEnum.REBIND_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "user-rebinds.rebind.success.title",
      bodyKey: "user-rebinds.rebind.success.body",
    },
  } as const)

export const rebindFailureAction = () =>
  ({
    type: UserRebindsActionsEnum.REBIND_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "user-rebinds.rebind.error.title",
      bodyKey: "user-rebinds.rebind.error.body",
    },
  } as const)

export const setViewingFullDocumentIdAction = (
  viewingFullDocumentId: number | null
) =>
  ({
    type: UserRebindsActionsEnum.SET_VIEWING_FULL_DOCUMENT_ID,
    payload: { viewingFullDocumentId },
  } as const)

export const setSelectedDocumentIdAction = (
  selectedDocumentId: number | null
) =>
  ({
    type: UserRebindsActionsEnum.SET_SELECTED_DOCUMENT_ID,
    payload: { selectedDocumentId },
  } as const)

export const toggleSplittingAction = (isSplitting: boolean) =>
  ({
    type: UserRebindsActionsEnum.TOGGLE_SPLITTING,
    payload: { isSplitting },
  } as const)

export const setSplitRequestStatusAction = (
  splitRequestStatus: SplitRequestStatus
) =>
  ({
    type: UserRebindsActionsEnum.SET_SPLIT_REQUEST_STATUS,
    payload: { splitRequestStatus },
  } as const)

type UserRebindsActionsType =
  | ReturnType<typeof loadBindAttemptAction>
  | ReturnType<typeof loadBindSuccessAction>
  | ReturnType<typeof loadBindFailureAction>
  | ReturnType<typeof toggleDisplayRebindContentAction>
  | ReturnType<typeof saveInitialDocumentsInFullDocsAction>
  | ReturnType<typeof updateDocumentsInFullDocsAction>
  | ReturnType<typeof resetDocumentsInFullDocsAction>
  | ReturnType<typeof rebindAttemptAction>
  | ReturnType<typeof rebindSuccessAction>
  | ReturnType<typeof rebindFailureAction>
  | ReturnType<typeof setViewingFullDocumentIdAction>
  | ReturnType<typeof setSelectedDocumentIdAction>
  | ReturnType<typeof toggleSplittingAction>
  | ReturnType<typeof setSplitRequestStatusAction>

export interface UserRebindsState {
  loading: boolean
  rebind_context: RebindContext | null
  error: boolean
  displayRebindContent: boolean
  initialDocumentsInFullDocs: DocumentInFullDocument[][] | null
  rebinding: boolean
  viewingFullDocumentId: number | null
  selectedDocumentId: number | null
  isSplitting: boolean
  splitRequestStatus: SplitRequestStatus
}

export const userRebindsInitialState: UserRebindsState = {
  loading: false,
  rebind_context: null,
  error: false,
  displayRebindContent: false,
  initialDocumentsInFullDocs: null,
  rebinding: false,
  viewingFullDocumentId: null,
  selectedDocumentId: null,
  isSplitting: false,
  splitRequestStatus: "idle",
}

export function userRebindsReducer(
  state = userRebindsInitialState,
  action: UserRebindsActionsType
): UserRebindsState {
  switch (action.type) {
    case UserRebindsActionsEnum.LOAD_BIND_ATTEMPT:
      return { ...state, loading: true, error: false }

    case UserRebindsActionsEnum.LOAD_BIND_SUCCESS:
      return {
        ...state,
        loading: false,
        rebind_context: action.payload,
        error: false,
        initialDocumentsInFullDocs: null,
      }

    case UserRebindsActionsEnum.LOAD_BIND_FAILURE:
      return { ...state, loading: false, error: true }

    case UserRebindsActionsEnum.TOGGLE_DISPLAY_REBIND_CONTENT:
      if (!action.payload.displayRebindContent) {
        return {
          ...state,
          displayRebindContent: false,
          loading: false,
          error: false,
          rebind_context: null,
          initialDocumentsInFullDocs: null,
          viewingFullDocumentId: null,
          selectedDocumentId: null,
          isSplitting: false,
          splitRequestStatus: "idle",
        }
      }
      return {
        ...state,
        displayRebindContent: action.payload.displayRebindContent,
      }

    case UserRebindsActionsEnum.UPDATE_DOCUMENTS_IN_FULL_DOCS:
      return {
        ...state,
        rebind_context: state.rebind_context
          ? {
              ...state.rebind_context,
              documents_in_full_docs: action.payload.documentsInFullDocs,
            }
          : null,
      }

    case UserRebindsActionsEnum.RESET_DOCUMENTS_IN_FULL_DOCS:
      return {
        ...state,
        rebind_context:
          state.rebind_context && state.initialDocumentsInFullDocs
            ? {
                ...state.rebind_context,
                documents_in_full_docs: state.initialDocumentsInFullDocs,
              }
            : state.rebind_context,
      }

    case UserRebindsActionsEnum.SAVE_INITIAL_DOCUMENTS_IN_FULL_DOCS:
      return {
        ...state,
        initialDocumentsInFullDocs: action.payload.initialDocumentsInFullDocs,
      }

    case UserRebindsActionsEnum.REBIND_ATTEMPT:
      return { ...state, rebinding: true }

    case UserRebindsActionsEnum.REBIND_SUCCESS:
      return { ...state, rebinding: false }

    case UserRebindsActionsEnum.REBIND_FAILURE:
      return { ...state, rebinding: false }

    case UserRebindsActionsEnum.SET_VIEWING_FULL_DOCUMENT_ID:
      return {
        ...state,
        viewingFullDocumentId: action.payload.viewingFullDocumentId,
      }

    case UserRebindsActionsEnum.SET_SELECTED_DOCUMENT_ID:
      return {
        ...state,
        selectedDocumentId: action.payload.selectedDocumentId,
      }

    case UserRebindsActionsEnum.TOGGLE_SPLITTING:
      return {
        ...state,
        isSplitting: action.payload.isSplitting,
      }

    case UserRebindsActionsEnum.SET_SPLIT_REQUEST_STATUS:
      return {
        ...state,
        splitRequestStatus: action.payload.splitRequestStatus,
      }

    default:
      return { ...state }
  }
}

export function sortDocumentsInFullDocsByPageOrder<
  T extends { document_id: number }
>(groups: T[][], documentsOrderedByPage: number[]): T[][] {
  const pageIndexByDocumentId = new Map<number, number>()
  documentsOrderedByPage.forEach((documentId, index) => {
    pageIndexByDocumentId.set(documentId, index)
  })
  const minPageIndex = (group: T[]) => {
    let min = Number.POSITIVE_INFINITY
    for (const doc of group) {
      const idx = pageIndexByDocumentId.get(doc.document_id)
      if (idx !== undefined && idx < min) min = idx
    }
    return min
  }
  return [...groups].sort((a, b) => minPageIndex(a) - minPageIndex(b))
}

export const loadBindThunk =
  (id: number): RNBThunkAction =>
  (dispatch: Dispatch<AnyAction>) => {
    dispatch(loadBindAttemptAction())
    return axios
      .get<RebindContextAPI>(`/user_rebinds/${id}/load_bind`)
      .then((response) => {
        const withFlags = response.data.documents_in_full_docs.map((group) =>
          group.map((doc) => ({
            ...doc,
            is_modified: false,
          }))
        )
        const rebindContext: RebindContext = {
          ...response.data,
          documents_in_full_docs: sortDocumentsInFullDocsByPageOrder(
            withFlags,
            response.data.documents
          ),
        }
        dispatch(loadBindSuccessAction(rebindContext))
      })
      .catch((e: AxiosError) => {
        dispatch(loadBindFailureAction())
      })
  }

export const rebindThunk =
  (
    id: number,
    documentsInFullDocs: DocumentInFullDocument[][]
  ): RNBThunkAction =>
  (dispatch: Dispatch<AnyAction | RNBThunkAction>) => {
    dispatch(rebindAttemptAction())

    return axios
      .post(`/user_rebinds/${id}/rebind`, {
        documents_in_full_docs: documentsInFullDocs,
      })
      .then(() => {
        dispatch(rebindSuccessAction())
        return (dispatch(loadBindThunk(id)) as unknown as Promise<void>).then(
          () => {
            dispatch(reloadInputMaskAfterRebindThunk())
          }
        )
      })
      .catch((e: AxiosError) => {
        dispatch(rebindFailureAction())
      })
  }

export interface SplitZone {
  x: number
  y: number
  width: number
  height: number
}

export const splitDocumentThunk =
  (
    batchDocumentId: number,
    documentId: number,
    zones: SplitZone[]
  ): RNBThunkAction =>
  (dispatch: Dispatch<AnyAction | RNBThunkAction>) => {
    dispatch(setSplitRequestStatusAction("loading"))
    return axios
      .post(`/batch_documents/${batchDocumentId}/split`, {
        document_id: documentId,
        zones: zones,
      })
      .then(() => {
        return (
          dispatch(loadBindThunk(batchDocumentId)) as unknown as Promise<void>
        ).then(() => {
          dispatch(reloadInputMaskAfterRebindThunk())
        })
      })
      .catch((e: AxiosError) => {
        throw e
      })
      .finally(() => {
        dispatch(setSplitRequestStatusAction("idle"))
      })
  }
